// redux/slices/takbullPaymentsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { resetStore } from "./resetStore";
import { apiRequest } from "../../Utils/apiClient";
import { clearJwtToken } from "../../Utils/authStorage";

/* ===================== HELPERS ===================== */

const getError = (error, fallback) =>
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback;

const handleAuthError = async (error) => {
    const status = error?.response?.status;

    if (status === 401 || status === 403) {
        try {
            await clearJwtToken();
        } catch { }
    }
};

const getUid = (getState, providedUid) => {
    const state = getState();

    const uid =
        providedUid ||
        state?.master?.master?.uid ||
        state?.master?.uid ||
        state?.auth?.uid ||
        null;

    if (!uid) throw new Error("UID required");

    return uid;
};

/* ===================== THUNKS ===================== */

export const createTakbullPayment = createAsyncThunk(
    "takbullPayments/create",
    async (paymentData = {}, { getState, rejectWithValue }) => {
        try {
            const uid = getUid(getState, paymentData.uid);

            const body = {
                uid,
                orderReference: paymentData.orderReference,
                amount: paymentData.amount,
                redirectUrl: paymentData.redirectUrl,
                ipnUrl: paymentData.ipnUrl,
                products: paymentData.products,
                createDocument: paymentData.createDocument,
                ...(paymentData.dealType !== undefined ? { dealType: paymentData.dealType } : {}),
                ...(paymentData.paymentMethodType !== undefined ? { paymentMethodType: paymentData.paymentMethodType } : {}),
                ...(paymentData.maxNumberOfPayments !== undefined ? { maxNumberOfPayments: paymentData.maxNumberOfPayments } : {}),
            };

            const res = await apiRequest("POST", "/payments/takbull/create", body);

            return res?.data || res;
        } catch (error) {
            await handleAuthError(error);
            return rejectWithValue(getError(error, "Create Takbull payment failed"));
        }
    }
);

export const fetchTakbullPayments = createAsyncThunk(
    "takbullPayments/fetch",
    async (uidArg, { getState, rejectWithValue }) => {
        try {
            const uid = getUid(getState, uidArg);

            const res = await apiRequest(
                "GET",
                `/payments/takbull/${encodeURIComponent(uid)}`
            );

            return res?.data || res;
        } catch (error) {
            await handleAuthError(error);
            return rejectWithValue(getError(error, "Fetch Takbull payments failed"));
        }
    }
);

/* ===================== STATE ===================== */

const initialState = {
    payments: [],
    status: "idle",
    creating: false,
    error: null,
};

/* ===================== SLICE ===================== */

const takbullPaymentsSlice = createSlice({
    name: "takbullPayments",
    initialState,
    reducers: {
        clearTakbullPaymentsError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(createTakbullPayment.pending, (state) => {
                state.creating = true;
                state.error = null;
            })
            .addCase(createTakbullPayment.fulfilled, (state, { payload }) => {
                state.creating = false;
                if (payload?.payment) {
                    state.payments.unshift(payload.payment);
                }
            })
            .addCase(createTakbullPayment.rejected, (state, action) => {
                state.creating = false;
                state.error =
                    action.payload ||
                    action.error?.message ||
                    "Create payment failed";
            })

            .addCase(fetchTakbullPayments.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(fetchTakbullPayments.fulfilled, (state, { payload }) => {
                state.status = "succeeded";
                state.payments = Array.isArray(payload) ? payload : [];
            })
            .addCase(fetchTakbullPayments.rejected, (state, action) => {
                state.status = "failed";
                state.error =
                    action.payload ||
                    action.error?.message ||
                    "Fetch failed";
            })

            .addCase(resetStore, () => initialState);
    },
});

/* ===================== ACTIONS ===================== */

export const { clearTakbullPaymentsError } = takbullPaymentsSlice.actions;

/* ===================== SELECTORS ===================== */

export const selectTakbullPayments = (state) =>
    state.takbullPayments || initialState;

export const selectTakbullPaymentCreating = (state) =>
    !!selectTakbullPayments(state).creating;

export const selectTakbullPaymentsError = (state) =>
    selectTakbullPayments(state).error || null;

export default takbullPaymentsSlice.reducer;